import { getIn } from "formik";

/**
 * This function determines whether a field should be rendered or not based on its visibility conditions and values.
 *
 * @param {object} field - The field object containing visibility conditions and other properties.
 * @param {string} arrObjName - The name of the array object.
 * @param {object} values - The values object containing the data for comparison (Formik Values).
 * @returns {boolean} - Returns true if the field should be rendered, otherwise false.
 */

export const shouldRenderFieldHelper = (field, arrObjName, formik) => {
  const { values, errors, setFieldValue, setFieldError } = formik;
  const { visibilityConditions, hidden, visibilityConditionLogic, name } =
    field;

  // If visibility conditions exist
  if (visibilityConditions) {
    // Check visibility conditions
    const isVisible =
      visibilityConditionLogic === "AND"
        ? visibilityConditions.every(conditionCheck(values, arrObjName, hidden))
        : visibilityConditions.some(conditionCheck(values, arrObjName, hidden));

    if (!isVisible) {
      // If the field is not visible, reset its value to null or an empty string.
      clearFieldValue(
        `${arrObjName}.${name}`,
        values,
        setFieldValue,
        setFieldError
      );
      clearFieldError(`${arrObjName}.${name}`, setFieldError, errors);
    }
    return isVisible;
  }
  return !hidden;
};

const conditionCheck = (values, arrObjName, hidden) => (condition) => {
  const comparisonValue = getIn(values, `${arrObjName}.${condition.name}`);
  // console.log("comparisonValue: ", comparisonValue);
  // console.log("condition.value: ", condition.value);
  // Check the type of condition and compare the values
  switch (condition.type) {
    case "==":
      return comparisonValue?.toString() === condition?.value?.toString();
    case "!=":
      return comparisonValue !== condition?.value;
    case ">":
      return comparisonValue > condition?.value;
    case ">=":
      return comparisonValue >= condition?.value;
    case "<":
      return comparisonValue < condition?.value;
    case "<=":
      return comparisonValue <= condition?.value;
    case "age<":
      return calculateAge(comparisonValue) < condition?.value;

    default:
      return !hidden;
  }
};

const clearFieldValue = (fieldName, values, setFieldValue, setFieldError) => {
  const fieldValue = getIn(values, fieldName);
  if (fieldValue) {
    setFieldError(fieldName, "");
    setFieldValue(fieldName, "");
  }
};

const clearFieldError = (fieldName, setFieldError, errors) => {
  // Clear the error for the field manually.
  if (errors && fieldName in errors) {
    setFieldError(fieldName, undefined);
  }
};

function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return;
  // Parse the DOB string into a Date object
  const dobDate = new Date(dateOfBirth);

  // Get the current date
  const currentDate = new Date();

  // Calculate the age
  const age = currentDate.getFullYear() - dobDate.getFullYear();

  // Check if the birthday has occurred this year
  if (
    currentDate.getMonth() < dobDate.getMonth() ||
    (currentDate.getMonth() === dobDate.getMonth() &&
      currentDate.getDate() < dobDate.getDate())
  ) {
    // If the birthday hasn't occurred yet this year, subtract 1 from the age
    return age - 1;
  }

  return age;
}
